import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { buildSeo } from "@/seo/build";
import content from "@/seo/content.json";

let cache = null;
const loadInfo = () => {
  cache = cache || Promise.all([api.get("/public/pricing"), api.get("/public/branding")]).then(([p, b]) => ({ ...p.data, ...b.data })).catch(() => ({}));
  return cache;
};

const applyHead = (title, tags) => {
  document.title = title;
  document.head.querySelectorAll("[data-seo]").forEach((el) => el.remove());
  tags.forEach(([tag, attrs, text]) => {
    const el = document.createElement(tag);
    Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
    el.setAttribute("data-seo", "true");
    if (text) el.textContent = text;
    document.head.appendChild(el);
  });
};

const DYNAMIC = ["/locations", "/industries", "/resources"];

// Dynamic public pages (city / industry / article) set their own head tags.
export const usePageSeo = (seo) => {
  const info = usePublicInfo();
  const key = seo ? JSON.stringify(seo) : "";
  useEffect(() => {
    if (!seo) return;
    const company = info.company_name || "";
    const title = company ? `${seo.title} | ${company}` : seo.title;
    const url = window.location.origin + seo.path;
    const image = window.location.origin + "/og-image.jpg";
    const meta = seo.noindex ? [["name", "robots", "noindex, follow"]] : [["name", "description", seo.description], ["name", "robots", "index, follow, max-image-preview:large"],
      ["property", "og:type", seo.type || "website"], ["property", "og:site_name", company], ["property", "og:title", title], ["property", "og:description", seo.description],
      ["property", "og:url", url], ["property", "og:image", image], ["name", "twitter:card", "summary_large_image"], ["name", "twitter:title", title],
      ["name", "twitter:description", seo.description], ["name", "twitter:image", image]];
    applyHead(title, [...(seo.noindex ? [] : [["link", { rel: "canonical", href: url }]]), ...meta.map(([k, n, c]) => ["meta", { [k]: n, content: c || "" }]),
      ...(seo.jsonLd || []).map((j) => ["script", { type: "application/ld+json" }, JSON.stringify(j)])]);
  }, [key, info.company_name]); // eslint-disable-line react-hooks/exhaustive-deps
};

const usePublicInfo = () => {
  const [info, setInfo] = useState({});
  useEffect(() => { loadInfo().then(setInfo); }, []);
  return info;
};

// Sets title, meta, canonical and JSON-LD per route; only public visitor pages are indexable.
export const RouteSeo = () => {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const info = usePublicInfo();
  useEffect(() => {
    const path = pathname.replace(/\/+$/, "") || "/";
    if (!user && DYNAMIC.some((p) => path === p || path.startsWith(p + "/"))) return;
    if (content.paths.includes(path) && !user) {
      const { title, url, meta, jsonLd } = buildSeo(path, info, window.location.origin, content);
      applyHead(title, [["link", { rel: "canonical", href: url }], ...meta.map(([k, n, c]) => ["meta", { [k]: n, content: c }]),
        ...jsonLd.map((j) => ["script", { type: "application/ld+json" }, JSON.stringify(j)])]);
    } else {
      applyHead(info.company_name || "Feasibility Studies", [["meta", { name: "robots", content: "noindex, nofollow" }]]);
    }
  }, [pathname, user, info]);
  return null;
};
